// Globaler Timer-Speicher (W8): ein Takt fuer alle Timer aller Rezepte, unabhaengig davon, welche Seite offen ist.
// Timer laufen weiter beim Blaettern, beim Verlassen des Kochmodus und nach dem Neuladen (localStorage).
// React liest den Stand ueber useSyncExternalStore (useTimer), die Anzeige tickt ueber useJetzt.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { benachrichtigen, gong, klingelnAn, klingelnAus } from './signal';
import { MAX_TIMER, timerAlle, timerSetzen, type KochTimer, type TimerErinnerung } from './speicher';

const TON_MS = 60_000;

export type UhrEreignis =
  | { art: 'klingelt'; timer: KochTimer }
  | { art: 'erinnerung'; timer: KochTimer; text: string };

let stand: readonly KochTimer[] = [];
const hoerer = new Set<() => void>();
const ereignisHoerer = new Set<(e: UhrEreignis) => void>();
let takt: number | null = null;
let geladen = false;

function laden(): void {
  stand = timerAlle().sort((a, b) => a.gestartet - b.gestartet);
  geladen = true;
}

function melden(): void {
  hoerer.forEach((h) => h());
}

function speichern(neu: readonly KochTimer[]): void {
  const rezepte = new Set([...stand.map((t) => t.rezeptId), ...neu.map((t) => t.rezeptId)]);
  for (const rid of rezepte) timerSetzen(rid, neu.filter((t) => t.rezeptId === rid));
  stand = [...neu];
  melden();
  pruefen();
}

function aendern(id: string, f: (t: KochTimer) => KochTimer | null): void {
  const neu: KochTimer[] = [];
  for (const t of stand) {
    if (t.id !== id) {
      neu.push(t);
      continue;
    }
    const x = f(t);
    if (x) neu.push(x);
  }
  speichern(neu);
}

/** Ein Takt: abgelaufene Timer klingeln lassen, Erinnerungen ausloesen, Ton nach 60 s aus, Signal steuern. */
function pruefen(): void {
  const jetzt = Date.now();
  let geaendert = false;
  const ereignisse: UhrEreignis[] = [];
  const neu = stand.map((t) => {
    let x = t;
    if (x.status === 'laeuft' && x.endetUm !== null) {
      const rest = x.endetUm - jetzt;
      for (const e of x.erinnerungen) {
        if (x.erinnert.includes(e.key) || rest <= 0) continue;
        const faellig = e.haelfte ? rest <= x.dauerMs / 2 : e.restMs !== null && rest <= e.restMs;
        if (faellig) {
          x = { ...x, erinnert: [...x.erinnert, e.key] };
          ereignisse.push({ art: 'erinnerung', timer: x, text: e.text });
          geaendert = true;
        }
      }
      if (rest <= 0) {
        x = { ...x, status: 'klingelt', klingeltSeit: x.endetUm, stumm: false, tonBis: jetzt + TON_MS, restMs: 0 };
        ereignisse.push({ art: 'klingelt', timer: x });
        geaendert = true;
      }
    }
    if (x.status === 'klingelt' && !x.stumm && x.tonBis !== null && jetzt > x.tonBis) {
      x = { ...x, stumm: true };
      geaendert = true;
    }
    return x;
  });
  if (geaendert) {
    stand = neu;
    const rezepte = new Set(neu.map((t) => t.rezeptId));
    for (const rid of rezepte) timerSetzen(rid, neu.filter((t) => t.rezeptId === rid));
    melden();
  }
  for (const e of ereignisse) {
    if (e.art === 'erinnerung') gong();
    if (e.art === 'klingelt' && document.visibilityState !== 'visible') {
      void benachrichtigen(
        `${e.timer.label.replace(/ \d+$/, '')} ist fertig`,
        e.timer.bedingung ? `${e.timer.rezeptName}: ${e.timer.bedingung}` : e.timer.rezeptName,
        `/rezept/${e.timer.rezeptSlug}/kochen?weiter=1`,
        e.timer.id,
      );
    }
    ereignisHoerer.forEach((h) => h(e));
  }
  if (stand.some((t) => t.status === 'klingelt' && !t.stumm)) klingelnAn();
  else klingelnAus();
  taktSteuern();
}

function taktSteuern(): void {
  const noetig = stand.some((t) => t.status === 'laeuft' || (t.status === 'klingelt' && !t.stumm));
  if (noetig && takt === null) takt = window.setInterval(pruefen, 250);
  if (!noetig && takt !== null) {
    window.clearInterval(takt);
    takt = null;
  }
}

function start(): void {
  if (geladen) return;
  laden();
  // Zurueck in die App: sofort nachrechnen, was inzwischen abgelaufen ist (Lyra SPEZ 6.1)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pruefen();
  });
  // Anderer Tab hat geaendert
  window.addEventListener('storage', (e) => {
    if (e.key?.startsWith('nb2.kochen.')) {
      laden();
      melden();
      pruefen();
    }
  });
  pruefen();
}

// ---------------------------------------------------------------- Aktionen

export interface TimerStart {
  rezeptId: string;
  rezeptSlug: string;
  rezeptName: string;
  schritt: number | null;
  index: number | null;
  label: string;
  ms: number;
  bisMs?: number | null;
  bedingung?: string | null;
  seiten?: number | null;
  seite?: number;
  erinnerungen?: TimerErinnerung[];
}

let zaehler = 0;

export const uhr = {
  starten(s: TimerStart): KochTimer | null {
    start();
    if (stand.filter((t) => t.status !== 'fertig').length >= MAX_TIMER) return null;
    const jetzt = Date.now();
    zaehler += 1;
    const t: KochTimer = {
      id: `t${jetzt.toString(36)}${zaehler}`,
      rezeptId: s.rezeptId,
      rezeptSlug: s.rezeptSlug,
      rezeptName: s.rezeptName,
      schritt: s.schritt,
      index: s.index,
      label: s.label,
      dauerMs: s.ms,
      endetUm: jetzt + s.ms,
      restMs: null,
      status: 'laeuft',
      klingeltSeit: null,
      stumm: false,
      tonBis: null,
      bisMs: s.bisMs ?? null,
      bedingung: s.bedingung ?? null,
      seiten: s.seiten ?? null,
      seite: s.seite ?? 1,
      // Erinnerungen, die schon beim Start faellig waeren (Timer kuerzer gestellt), entfallen
      erinnerungen: (s.erinnerungen ?? []).filter((e) => e.haelfte || (e.restMs !== null && e.restMs < s.ms - 1000)),
      erinnert: [],
      gestartet: jetzt,
    };
    // Ein erkannter Timer laeuft je Schritt nur einmal: alter Durchgang (fertig) wird ersetzt
    const ohneAlten = stand.filter(
      (x) => !(x.schritt !== null && x.rezeptId === t.rezeptId && x.schritt === t.schritt && x.index === t.index),
    );
    speichern([...ohneAlten, t]);
    return t;
  },
  pausieren(id: string): void {
    aendern(id, (t) => (t.status === 'laeuft' && t.endetUm ? { ...t, status: 'pausiert', restMs: Math.max(0, t.endetUm - Date.now()), endetUm: null } : t));
  },
  fortsetzen(id: string): void {
    aendern(id, (t) => (t.status === 'pausiert' && t.restMs !== null ? { ...t, status: 'laeuft', endetUm: Date.now() + t.restMs, restMs: null } : t));
  },
  /** +1 Min (laufend oder pausiert), +3 Min nach dem Klingeln: laeuft dann wieder */
  verlaengern(id: string, ms: number): void {
    aendern(id, (t) => {
      if (t.status === 'laeuft' && t.endetUm) return { ...t, endetUm: t.endetUm + ms, dauerMs: t.dauerMs + ms };
      if (t.status === 'pausiert' && t.restMs !== null) return { ...t, restMs: t.restMs + ms, dauerMs: t.dauerMs + ms };
      if (t.status === 'klingelt' || t.status === 'fertig')
        return { ...t, status: 'laeuft', endetUm: Date.now() + ms, dauerMs: ms, restMs: null, klingeltSeit: null, stumm: false, tonBis: null };
      return t;
    });
  },
  /** Stopp: Timer ist weg (Karte zeigt wieder "bereit") */
  stoppen(id: string): void {
    aendern(id, () => null);
  },
  /** "Ton aus" im Fertig-Sheet: Timer ist fertig, Karte zeigt "Köcheln fertig" */
  tonAus(id: string): void {
    aendern(id, (t) => (t.status === 'klingelt' ? { ...t, status: 'fertig', stumm: true } : t));
  },
  /** "2. Seite starten": gleicher Timer, gleiche Dauer, zweite Seite */
  zweiteSeite(id: string): void {
    aendern(id, (t) => ({
      ...t,
      status: 'laeuft',
      seite: 2,
      label: t.label.replace('1. Seite', '2. Seite'),
      endetUm: Date.now() + t.dauerMs,
      restMs: null,
      klingeltSeit: null,
      stumm: false,
      tonBis: null,
      erinnert: [],
    }));
  },
  /** Fertige Timer eines Rezepts wegraeumen (Abschluss, "Von vorn") */
  rezeptBeenden(rezeptId: string, auchLaufende: boolean): void {
    speichern(stand.filter((t) => t.rezeptId !== rezeptId || (!auchLaufende && t.status !== 'fertig')));
  },
  onEreignis(h: (e: UhrEreignis) => void): () => void {
    ereignisHoerer.add(h);
    return () => ereignisHoerer.delete(h);
  },
  /** Nach dem Abmelden: Speicher ist geleert, Takt und Ton aus */
  neuLaden(): void {
    laden();
    melden();
    pruefen();
  },
};

function abonnieren(h: () => void): () => void {
  start();
  hoerer.add(h);
  return () => hoerer.delete(h);
}

function schnappschuss(): readonly KochTimer[] {
  start();
  return stand;
}

/** Alle Timer (aller Rezepte), neu bei jeder Zustandsaenderung. */
export function useTimer(): readonly KochTimer[] {
  return useSyncExternalStore(abonnieren, schnappschuss, schnappschuss);
}

/** Uhrzeit fuer die Anzeige, tickt alle 250 ms solange aktiv (Restzeit = endetUm - jetzt). */
export function useJetzt(aktiv: boolean): number {
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!aktiv) return;
    setJetzt(Date.now());
    const t = window.setInterval(() => setJetzt(Date.now()), 250);
    return () => window.clearInterval(t);
  }, [aktiv]);
  return jetzt;
}

export function restMs(t: KochTimer, jetzt: number): number {
  if (t.status === 'laeuft' && t.endetUm !== null) return Math.max(0, t.endetUm - jetzt);
  if (t.status === 'pausiert' && t.restMs !== null) return t.restMs;
  return 0;
}
